import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, Sprout, CalendarRange, Scale, Info, Download, Share2 } from "lucide-react";
import { api } from "../../lib/api";
import { formatRupiah } from "../../lib/labels";
import { PageHero } from "./PageHero";
import { Button } from "../../components/ui/button";

export const PARTY_LABEL = { PEMODAL: "Pemodal", PENGELOLA: "Pengelola", PEMILIK_LAHAN: "Pemilik Lahan", PT_SJI: "PT SJI" };
const SCEN_LABEL = { KONSERVATIF: "Konservatif", MODERAT: "Moderat", OPTIMISTIS: "Optimistis" };
const PHASE = { BELUM_PANEN: "Belum panen", TIDAK_ADA_LABA: "Tidak ada laba", SEBELUM_BEP: "Sebelum BEP", PERALIHAN_BEP: "BEP tercapai (dipisah)", SETELAH_BEP: "Setelah BEP" };
const pct = (bp) => `${(bp / 100).toLocaleString("id-ID")}%`;
const kg = (n) => `${Number(n).toLocaleString("id-ID")} kg`;
const Wrap = ({ children, className = "", testid }) => <section className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 ${className}`} data-testid={testid}>{children}</section>;
const H2 = ({ letter, children }) => <h2 className="text-lg md:text-xl font-extrabold text-emerald-950 mb-4"><span className="mr-2 text-emerald-700">{letter}.</span>{children}</h2>;

const RiskWarning = () => (
  <div className="rounded-2xl border-2 border-amber-400 bg-amber-50 p-5 sm:p-6 text-sm text-amber-950 space-y-3" data-testid="sim-risk-warning">
    <p className="flex items-center gap-2 text-base font-extrabold"><AlertTriangle className="h-5 w-5" />PENTING! SIMULASI PENDANAAN, BUKAN JAMINAN KEUNTUNGAN.</p>
    <p>Seluruh angka yang ditampilkan merupakan asumsi perencanaan berdasarkan perkiraan produksi, biaya operasional, harga jual, dan kondisi perkebunan kopi.</p>
    <p>Hasil perkebunan dapat meningkat atau menurun akibat cuaca, hama, penyakit tanaman, kondisi tanah, pemeliharaan, perubahan harga pasar, dan faktor lainnya.</p>
    <p>Pemodal berpotensi memperoleh keuntungan, mengalami kerugian, atau tidak memperoleh kembali seluruh modal hingga kontrak berakhir.</p>
    <div><p className="font-bold">PT SJI TIDAK MENJAMIN:</p>
      <ul className="ml-5 list-disc"><li>Pengembalian modal.</li><li>Keuntungan.</li><li>Waktu tercapainya BEP.</li><li>Keberhasilan kebun.</li></ul></div>
    <p>PT SJI tidak memaksa atau mewajibkan siapa pun menjadi Pemodal. Setiap calon Pemodal bebas mempelajari informasi kebun, mempertimbangkan risiko, dan memutuskan secara sukarela apakah akan berpartisipasi. Keputusan pendanaan sepenuhnya berada pada calon Pemodal.</p>
  </div>
);

const Picker = ({ options, value, onChange, render, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {options.map((o) => (
      <button key={o} type="button" onClick={() => onChange(o)} data-testid={`${testid}-${o}`}
        className={`rounded-full border px-5 py-2.5 text-sm font-bold transition-colors ${value === o ? "border-emerald-800 bg-emerald-800 text-white" : "border-emerald-900/20 bg-white text-emerald-950 hover:bg-emerald-50"}`}>{render(o)}</button>
    ))}
  </div>
);

const Row = ({ k, v, strong, testid }) => (
  <div className="flex justify-between gap-3 border-b border-slate-100 py-1.5 last:border-0">
    <span className="text-slate-600">{k}</span><span className={`text-right font-mono ${strong ? "font-bold text-slate-900" : "text-slate-800"}`} data-testid={testid}>{v}</span>
  </div>
);

const ScenarioCard = ({ s, d, price, r }) => {
  const stable = d.kg[s] * price;
  return (
    <div className="rounded-2xl border border-emerald-900/10 bg-white p-5 text-sm" data-testid={`sim-scenario-${s}`}>
      <p className="text-xs font-bold uppercase tracking-widest text-emerald-700">{SCEN_LABEL[s]}</p>
      <p className="mt-1 text-2xl font-extrabold text-slate-900">{kg(d.kg[s])}<span className="text-sm font-semibold text-slate-500">/tahun</span></p>
      <div className="mt-3">
        <Row k="Modal awal" v={formatRupiah(d.capital)} />
        <Row k="Pendapatan stabil/tahun" v={formatRupiah(stable)} />
        <Row k="Biaya operasional stabil/tahun" v={formatRupiah(d.opex)} />
        <Row k="Laba stabil/tahun" v={formatRupiah(stable - d.opex)} />
        <Row k={`Estimasi BEP (kontrak ${r.term} th)`} v={r.bepReached ? `Tahun ke-${r.bepYear}` : "Tidak tercapai"} strong testid={`sim-scenario-${s}-bep`} />
      </div>
    </div>
  );
};

const BepTable = ({ data, term }) => (
  <div className="overflow-x-auto rounded-2xl border border-emerald-900/10 bg-white"><table className="w-full min-w-[620px] text-sm" data-testid="sim-bep-table">
    <thead className="bg-emerald-50 text-left text-xs uppercase text-emerald-900"><tr><th className="p-3">Kepadatan</th>{data.scenarios.map((s) => <th key={s} className="p-3">{SCEN_LABEL[s]}</th>)}</tr></thead>
    <tbody>{data.params.densities.map((d) => (
      <tr key={d.trees} className="border-t border-slate-100">
        <td className="p-3 font-semibold">{d.trees.toLocaleString("id-ID")} batang</td>
        {data.scenarios.map((s) => {
          const r = data.results.find((x) => x.trees === d.trees && x.scenario === s && x.term === term);
          return (
            <td key={s} className="p-3" data-testid={`sim-bep-${d.trees}-${s}`}>
              <b>{r.bepReached ? `Tahun ke-${r.bepYear}` : "Tidak tercapai"}</b>
              <p className="text-xs text-slate-500">Estimasi awal: {r.initialBep ? `tahun ke-${r.initialBep}` : "-"}{r.bepDiff ? ` (selisih ${r.bepDiff > 0 ? "+" : ""}${r.bepDiff} th)` : r.initialBep && r.bepReached ? " · sesuai" : ""}</p>
            </td>
          );
        })}
      </tr>))}</tbody>
  </table></div>
);

const SplitTable = ({ params, parties }) => (
  <div className="overflow-x-auto rounded-2xl border border-emerald-900/10 bg-white"><table className="w-full min-w-[520px] text-sm" data-testid="sim-split-table">
    <thead className="bg-emerald-50 text-left text-xs uppercase text-emerald-900"><tr><th className="p-3">Fase</th>{parties.map((p) => <th key={p} className="p-3">{PARTY_LABEL[p]}</th>)}<th className="p-3">Total</th></tr></thead>
    <tbody className="font-mono">{[["Sebelum BEP", params.pre], ["Setelah BEP", params.post]].map(([l, bp]) => (
      <tr key={l} className="border-t border-slate-100"><td className="p-3 font-sans font-semibold">{l}</td>{parties.map((p) => <td key={p} className="p-3">{pct(bp[p])}</td>)}<td className="p-3 font-bold">{pct(Object.values(bp).reduce((a, b) => a + b, 0))}</td></tr>))}</tbody>
  </table></div>
);

const TotalsCard = ({ r }) => (
  <div className="rounded-2xl border border-emerald-900/10 bg-white p-5 text-sm" data-testid={`sim-totals-${r.scenario}`}>
    <p className="text-xs font-bold uppercase tracking-widest text-emerald-700">{SCEN_LABEL[r.scenario]} · {r.term} tahun</p>
    <div className="mt-3">
      <Row k="Modal awal Pemodal" v={formatRupiah(r.capital)} />
      <Row k="Estimasi tahun BEP" v={r.bepReached ? `Tahun ke-${r.bepYear}` : "Tidak tercapai"} />
      <Row k="Status BEP" v={r.bepReached ? "Tercapai dalam kontrak" : "Tidak tercapai dalam kontrak"} testid={`sim-totals-${r.scenario}-status`} />
      <Row k="Total penerimaan Pemodal" v={formatRupiah(r.totals.PEMODAL)} strong testid={`sim-totals-${r.scenario}-pemodal`} />
      <Row k={r.pemodalProfit >= 0 ? "Keuntungan nominal Pemodal" : "Kekurangan modal Pemodal"} v={formatRupiah(r.pemodalProfit)} strong testid={`sim-totals-${r.scenario}-profit`} />
      <Row k="Total penerimaan Pengelola" v={formatRupiah(r.totals.PENGELOLA)} />
      <Row k="Total penerimaan Pemilik Lahan" v={formatRupiah(r.totals.PEMILIK_LAHAN)} />
      <Row k="Total penerimaan PT SJI" v={formatRupiah(r.totals.PT_SJI)} />
    </div>
  </div>
);

const CashflowTable = ({ r, parties }) => (
  <div className="overflow-x-auto rounded-2xl border border-emerald-900/10 bg-white"><table className="w-full min-w-[1100px] text-xs" data-testid="sim-cashflow-table">
    <thead className="bg-emerald-50 text-left uppercase text-emerald-900"><tr>
      <th className="p-2">Tahun</th><th className="p-2">Produksi</th><th className="p-2">Pendapatan</th><th className="p-2">Biaya Operasional</th><th className="p-2">Laba</th><th className="p-2">Fase</th>
      {parties.map((p) => <th key={p} className="p-2">{PARTY_LABEL[p]}</th>)}<th className="p-2">Akumulasi Pemodal</th></tr></thead>
    <tbody className="font-mono">{r.rows.map((y) => (
      <tr key={y.year} className={`border-t border-slate-100 ${y.bepThisYear ? "bg-emerald-50" : ""}`} data-testid={`sim-cashflow-row-${y.year}`}>
        <td className="p-2 font-sans font-semibold">{y.year}</td><td className="p-2">{y.productionPct}% · {kg(y.kg)}</td><td className="p-2">{formatRupiah(y.revenue)}</td>
        <td className="p-2">{y.opexPct}% · {formatRupiah(y.opex)}</td><td className={`p-2 ${y.profit < 0 ? "text-red-700" : ""}`}>{formatRupiah(y.profit)}</td>
        <td className="p-2 font-sans">{PHASE[y.phase]}</td>{parties.map((p) => <td key={p} className="p-2">{formatRupiah(y.shares[p])}</td>)}
        <td className="p-2 font-bold">{formatRupiah(y.pemodalCumulative)}</td>
      </tr>))}</tbody>
  </table></div>
);

const PdfButton = ({ trees, scen, term }) => (
  <div className="flex flex-col items-start gap-1 sm:items-end">
    <Button asChild variant="outline" className="h-11 rounded-full border-emerald-800 px-5 font-bold text-emerald-900" data-testid="sim-download-pdf-btn">
      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/public/asumsi-bagi-hasil/pdf?trees=${trees}&scenario=${scen}&term=${term}`} download>
        <Download className="mr-2 h-4 w-4" />UNDUH SIMULASI PDF</a>
    </Button>
    <span className="text-xs text-slate-500" data-testid="sim-download-pdf-combo">{trees.toLocaleString("id-ID")} batang · {SCEN_LABEL[scen]} · {term} tahun</span>
  </div>
);

const CompareButton = ({ trees, term }) => (
  <div className="mt-4 flex flex-col items-start gap-1">
    <Button asChild variant="outline" className="h-11 rounded-full border-emerald-800 px-5 font-bold text-emerald-900" data-testid="sim-download-compare-pdf-btn">
      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/public/asumsi-bagi-hasil/pdf-compare?trees=${trees}&term=${term}`} download>
        <Download className="mr-2 h-4 w-4" />UNDUH PERBANDINGAN 3 SKENARIO (PDF)</a>
    </Button>
    <span className="text-xs text-slate-500" data-testid="sim-download-compare-combo">{trees.toLocaleString("id-ID")} batang · {term} tahun · Konservatif, Moderat, Optimistis</span>
  </div>
);

const shareText = (trees, scen, term) => {
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/share/asumsi-bagi-hasil?pohon=${trees}&skenario=${scen}&kontrak=${term}`;
  return `Simulasi Asumsi Bagi Hasil Salimbado Farm: ${trees.toLocaleString("id-ID")} batang/ha, skenario ${SCEN_LABEL[scen]}, kontrak ${term} tahun. `
    + `Simulasi pendanaan, bukan jaminan keuntungan.\n${url}`;
};

const ShareButton = ({ trees, scen, term }) => (
  <Button asChild className="h-11 rounded-full bg-[#1f8f4e] px-5 font-bold text-white hover:bg-[#18733f]" data-testid="sim-share-wa-btn">
    <a href={`https://wa.me/?text=${encodeURIComponent(shareText(trees, scen, term))}`} target="_blank" rel="noreferrer">
      <Share2 className="mr-2 h-4 w-4" />BAGIKAN VIA WHATSAPP</a>
  </Button>
);

export default function AsumsiBagiHasil() {
  const [qs, setQs] = useSearchParams();
  const [data, setData] = useState(null);
  const [trees, setTrees] = useState(null);
  const [term, setTerm] = useState(null);
  const [scen, setScen] = useState("KONSERVATIF");
  useEffect(() => {
    api.get("/public/asumsi-bagi-hasil").then((r) => {
      const p = r.data.params;
      const t = Number(qs.get("pohon")), k = Number(qs.get("kontrak")), s = qs.get("skenario");
      setData(r.data);
      setTrees(p.densities.some((d) => d.trees === t) ? t : p.densities[0].trees);
      setTerm(p.terms.includes(k) ? k : p.terms[0]);
      if (r.data.scenarios.includes(s)) setScen(s);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (trees && term) { setQs({ pohon: trees, skenario: scen, kontrak: term }, { replace: true }); }
  }, [trees, term, scen, setQs]);
  const pick = useMemo(() => (s) => data?.results.find((x) => x.trees === trees && x.scenario === s && x.term === term), [data, trees, term]);
  if (!data || !trees) return <div className="p-10 text-center text-slate-500" data-testid="sim-loading">Memuat asumsi…</div>;
  const { params, scenarios, parties } = data;
  const d = params.densities.find((x) => x.trees === trees);
  return (
    <div className="bg-[#f7f5ef]" data-testid="asumsi-bagi-hasil-page">
      <PageHero eyebrow="Simulasi Resmi Salimbado Farm" title="ASUMSI BAGI HASIL" image="/images/coffee-seedling.jpg"
        intro="Pusat resmi simulasi pendanaan dan pembagian hasil kebun kopi Robusta unggul. Pelajari seluruh asumsi, estimasi, dan risiko sebelum mengambil keputusan." />
      <Wrap className="pt-10"><RiskWarning /></Wrap>
      <Wrap><H2 letter="C"><Sprout className="mr-1 inline h-5 w-5" />Pilihan jumlah tanaman per hektare</H2>
        <Picker options={params.densities.map((x) => x.trees)} value={trees} onChange={setTrees} render={(t) => `${t.toLocaleString("id-ID")} batang`} testid="sim-density" />
        <p className="mt-2 text-xs text-slate-500">Harga jual asumsi {formatRupiah(params.pricePerKg)}/kg green bean · versi parameter {params.version}</p></Wrap>
      <Wrap><H2 letter="D">Tiga skenario produksi</H2>
        <div className="grid gap-4 md:grid-cols-3">{scenarios.map((s) => <ScenarioCard key={s} s={s} d={d} price={params.pricePerKg} r={pick(s)} />)}</div></Wrap>
      <Wrap><H2 letter="E"><CalendarRange className="mr-1 inline h-5 w-5" />Pilihan masa kontrak</H2>
        <Picker options={params.terms} value={term} onChange={setTerm} render={(t) => `${t} TAHUN`} testid="sim-term" /></Wrap>
      <Wrap><H2 letter="F">Perbandingan estimasi BEP (kontrak {term} tahun)</H2><BepTable data={data} term={term} />
        <p className="mt-2 text-xs text-slate-500">BEP dihitung ulang dari arus kas simulasi: tercapai saat akumulasi penerimaan Pemodal mencapai modal awal.</p></Wrap>
      <Wrap><H2 letter="G"><Scale className="mr-1 inline h-5 w-5" />Pembagian hasil empat pihak</H2><SplitTable params={params} parties={parties} />
        <p className="mt-2 text-xs text-slate-500">Dihitung dari laba kebun setelah biaya operasional. Bila BEP tercapai di tengah tahun, laba tahun itu dipisah secara proporsional: bagian sebelum BEP sampai modal Pemodal kembali, sisanya memakai persentase setelah BEP. Tahun tanpa laba tidak menghasilkan pembagian.</p></Wrap>
      <Wrap><H2 letter="H">Total proyeksi penerimaan selama kontrak</H2>
        <div className="grid gap-4 md:grid-cols-3">{scenarios.map((s) => <TotalsCard key={s} r={pick(s)} />)}</div>
        <CompareButton trees={trees} term={term} /></Wrap>
      <Wrap><H2 letter="I">Rincian arus kas tahunan</H2>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Picker options={scenarios} value={scen} onChange={setScen} render={(s) => SCEN_LABEL[s]} testid="sim-cashflow-scenario" />
          <div className="flex flex-wrap items-start gap-3"><PdfButton trees={trees} scen={scen} term={term} /><ShareButton trees={trees} scen={scen} term={term} /></div>
        </div>
        <div className="mt-4"><CashflowTable r={pick(scen)} parties={parties} /></div>
        <p className="mt-2 text-xs text-slate-500">Produksi: {params.productionPct.map((v, i) => `th ${i + 1}${i === params.productionPct.length - 1 ? "+" : ""} ${v}%`).join(" · ")}. Biaya operasional: {params.opexPct.map((v, i) => `th ${i + 1}${i === params.opexPct.length - 1 ? "+" : ""} ${v}%`).join(" · ")} (tahun 1–2 sudah termasuk modal awal).</p></Wrap>
      <Wrap className="pb-16"><H2 letter="J"><Info className="mr-1 inline h-5 w-5" />Hasil aktual dapat berbeda</H2>
        <div className="rounded-2xl border border-emerald-900/10 bg-white p-5 text-sm text-slate-700 space-y-2" data-testid="sim-actual-note">
          <p>Simulasi ini adalah proyeksi dari angka asumsi, bukan hasil pasti. Realisasi keuangan setiap kebun dicatat terpisah dari simulasi dan mengikuti Perjanjian Kebun yang berlaku.</p>
          <p>Perubahan asumsi oleh PT SJI tidak mengubah transaksi aktual atau ketentuan kontrak yang sudah berlaku.</p>
        </div>
        <Button asChild className="mt-6 h-12 rounded-full bg-emerald-800 px-6 font-bold hover:bg-emerald-900" data-testid="sim-view-lands-btn"><Link to="/peluang-investasi">LIHAT RENCANA KEBUN TERSEDIA</Link></Button></Wrap>
    </div>
  );
}
